/**
 * Same reminder rules as Dashboard "Reminders" card — keep in sync when changing either place.
 */

/** localStorage: set to '1' to hide the post-deploy migration checklist on Dashboard + nav badge. */
export const POST_DEPLOY_MIGRATIONS_REMINDER_DISMISS_KEY =
  'family-fin-dismiss-reminder-post-deploy-migrations-2026-04';

/** Fired when Dashboard finishes loading data that affects reminder count (Layout listens to refresh badge). */
export const DASHBOARD_REMINDERS_CHANGED_EVENT = 'family-fin-dashboard-reminders-changed';

/** True when the post-deploy migration checklist should appear (not dismissed in localStorage). */
export function shouldShowPostDeployMigrationsReminder(): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return false;
  }
  try {
    return localStorage.getItem(POST_DEPLOY_MIGRATIONS_REMINDER_DISMISS_KEY) !== '1';
  } catch {
    return false;
  }
}

export interface ManualReviewSummary {
  manual_reviews_required: number;
  untagged_transactions: number;
  conflicting_vat_transactions: number;
  total_revolut_transactions?: number;
  tagged_revolut_transactions?: number;
}

export interface SupplierVatPolicyRow {
  supplier_id: number;
  name: string;
  is_active: boolean;
  vat_invoice_lookback_months: number | null;
  vat_invoices_last_retrieved_at: string | null;
  retrieval_warning: string | null;
}

export interface DashboardReminder {
  key: string;
  source: 'VAT' | 'Review' | 'Ops' | 'Tasks';
  title: string;
  detail: string;
  dueDate: Date | null;
  severity: 'warning' | 'info' | 'error';
  /** When set, Dashboard shows a Dismiss control that writes this key to localStorage. */
  dismissStorageKey?: string;
}

/** Deep-link for the header notifications panel (and Dashboard reminder actions). */
export function pathForDashboardReminder(reminder: Pick<DashboardReminder, 'key' | 'source'>): string {
  if (reminder.source === 'Review' || reminder.key === 'review-queue-open') {
    return '/transaction-classification-review';
  }
  if (reminder.source === 'VAT' || reminder.source === 'Tasks') {
    return '/dashboard';
  }
  return '/dashboard';
}

export interface RecurringTaskReminderInput {
  id: number;
  title: string;
  detail?: string | null;
  due_on: string;
  deadline_on: string;
  status: string;
}

function formatTaskDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day} ${months[month - 1]} ${year}`;
}

/** Open recurring tasks stay in the notifications badge until they are checked off. */
export function remindersFromRecurringTasks(tasks: RecurringTaskReminderInput[]): DashboardReminder[] {
  const today = new Date();
  const atMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return tasks
    .filter((task) => task.status === 'open')
    .map((task) => {
      const deadline = new Date(`${task.deadline_on}T00:00:00`);
      const due = new Date(`${task.due_on}T00:00:00`);
      const pastDeadline = !Number.isNaN(deadline.getTime()) && deadline.getTime() < atMidnight.getTime();
      const detailParts = [
        task.detail || '',
        task.due_on ? `Due ${formatTaskDate(task.due_on)}` : '',
        task.deadline_on ? `Deadline ${formatTaskDate(task.deadline_on)}` : '',
      ].filter(Boolean);
      return {
        key: `recurring-task-${task.id}`,
        source: 'Tasks' as const,
        title: task.title,
        detail: detailParts.join(' · '),
        dueDate: Number.isNaN(due.getTime()) ? null : due,
        severity: pastDeadline ? 'error' : 'warning',
      };
    });
}

export function buildDashboardReminders(
  manualReviewSummary: ManualReviewSummary | null,
  supplierVatPolicyRows: SupplierVatPolicyRow[],
  /** When set, post-deploy / ops reminders are only added for owner and admin. */
  viewerRole?: string | null
): DashboardReminder[] {
  const reminders: DashboardReminder[] = [];
  const today = new Date();
  const atMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const addMonths = (base: Date, months: number): Date => {
    const d = new Date(base);
    const targetMonth = d.getMonth() + months;
    const year = d.getFullYear() + Math.floor(targetMonth / 12);
    const month = ((targetMonth % 12) + 12) % 12;
    const day = d.getDate();
    const monthLastDay = new Date(year, month + 1, 0).getDate();
    return new Date(year, month, Math.min(day, monthLastDay));
  };

  for (const row of supplierVatPolicyRows) {
    if (!row?.is_active) continue;
    const lookback = row.vat_invoice_lookback_months ?? null;
    const lastRaw = row.vat_invoices_last_retrieved_at;
    if (lookback && lastRaw) {
      const lastDate = new Date(`${lastRaw}T00:00:00`);
      if (!Number.isNaN(lastDate.getTime())) {
        const dueDate = addMonths(lastDate, lookback);
        const daysUntil = Math.floor((dueDate.getTime() - atMidnight.getTime()) / (1000 * 60 * 60 * 24));
        const severity: DashboardReminder['severity'] =
          daysUntil < 0 ? 'error' : daysUntil <= 30 ? 'warning' : 'info';
        reminders.push({
          key: `vat-${row.supplier_id}-due`,
          source: 'VAT',
          title: `${row.name}: VAT invoice retrieval due`,
          detail: `Next retrieval due on ${dueDate.toLocaleDateString()}. Last retrieved: ${lastDate.toLocaleDateString()} (${lookback} month lookback).`,
          dueDate,
          severity,
        });
      }
    } else if (lookback && !lastRaw) {
      reminders.push({
        key: `vat-${row.supplier_id}-missing-last`,
        source: 'VAT',
        title: `${row.name}: VAT reminder setup incomplete`,
        detail: 'Set "Last VAT invoices retrieved" to calculate the next reminder date.',
        dueDate: null,
        severity: 'warning',
      });
    }
    if (row.retrieval_warning) {
      reminders.push({
        key: `vat-${row.supplier_id}-warning`,
        source: 'VAT',
        title: `${row.name}: VAT lookback warning`,
        detail: row.retrieval_warning,
        dueDate: null,
        severity: 'warning',
      });
    }
  }

  if ((manualReviewSummary?.manual_reviews_required || 0) > 0) {
    reminders.push({
      key: 'review-queue-open',
      source: 'Review',
      title: 'Transaction classification reviews pending',
      detail: `${manualReviewSummary?.manual_reviews_required ?? 0} items need review (${manualReviewSummary?.untagged_transactions ?? 0} untagged, ${manualReviewSummary?.conflicting_vat_transactions ?? 0} VAT conflicts).`,
      dueDate: null,
      severity: 'warning',
    });
  }

  const canSeeOpsReminders = viewerRole === 'owner' || viewerRole === 'admin';
  if (canSeeOpsReminders && shouldShowPostDeployMigrationsReminder()) {
    reminders.push({
      key: 'post-deploy-sql-migrations-apr2026',
      source: 'Ops',
      title: 'Post-deploy: run one-off database migration scripts',
      detail:
        'Deploys ship code only; these scripts change Postgres data. From a shell with DATABASE_URL pointing at production (Hetzner tunnel or VPS), run: ' +
        '(1) python3 migrations/move_supplier_alias_merchant_to_description_match.py ' +
        '(2) python3 migrations/move_classification_rule_merchant_to_description_match.py ' +
        '(3) python3 migrations/merge_classification_rules_by_output.py — use --dry-run first on each. ' +
        'Repo: backend/migrations/.',
      dueDate: null,
      severity: 'warning',
      dismissStorageKey: POST_DEPLOY_MIGRATIONS_REMINDER_DISMISS_KEY,
    });
  }

  return reminders.sort((a, b) => {
    if (a.dueDate && b.dueDate) return a.dueDate.getTime() - b.dueDate.getTime();
    if (a.dueDate && !b.dueDate) return -1;
    if (!a.dueDate && b.dueDate) return 1;
    return a.title.localeCompare(b.title);
  });
}
